/**
 * Session roles API client (M-X12).
 *
 * Talks to /api/sessions/:sessionId/roles — the multi-role composer
 * endpoints. Adding a role here is what wires it into the next message
 * (system prompt cascade + /knowledge/roles/<slug>/ bind-mount).
 * Removing a role flips active=false and the next run drops the mount;
 * the chip vanishes immediately from the UI.
 *
 * Backend: apps/chaos-gateway/src/session-roles.ts
 */
import type { RolePreselectState } from '@chaos/types/role-model-preselect';
import { apiJson } from './client';

export interface SessionRole {
  sessionId: string;
  roleId: string;
  roleSlug: string;
  roleDisplayName: string;
  roleIcon: string | null;
  textColor: string;
  active: boolean;
  addedAt: string;
  addedBy: string | null;
  // M-X16: per-role default vendor/model/effort surfaced alongside the
  // chip so toasts and tooltips can show "settings applied" hints.
  // Null = role has no preference for that dimension.
  defaultVendor: string | null;
  defaultModel: string | null;
  defaultEffort: string | null;
  /**
   * Founder 2026-10-04: how this role's defaults relate to the card's picker
   * now — applied / outranked (a more advanced role default won) / manual
   * (the card holds a manual choice) / none. Optional: older gateways omit it.
   */
  preselect?: { model: RolePreselectState; effort: RolePreselectState };
}

interface ListResponse {
  items: SessionRole[];
}

export const sessionRolesApi = {
  list(sessionId: string): Promise<SessionRole[]> {
    return apiJson<ListResponse>(`/sessions/${sessionId}/roles`).then(
      (r) => r.items,
    );
  },
  add(sessionId: string, roleId: string, principalFence: string): Promise<SessionRole[]> {
    return apiJson<ListResponse>(`/sessions/${sessionId}/roles`, {
      method: 'POST',
      json: { roleId },
      headers: { 'X-Chaos-Expected-Principal': principalFence },
    }).then((r) => r.items);
  },
  remove(sessionId: string, roleId: string, principalFence: string): Promise<SessionRole[]> {
    return apiJson<ListResponse>(`/sessions/${sessionId}/roles/${roleId}`, {
      method: 'DELETE',
      headers: { 'X-Chaos-Expected-Principal': principalFence },
    }).then((r) => r.items);
  },
};

export const sessionRolesKeys = {
  all: ['session-roles'] as const,
  forSession: (sessionId: string) =>
    [...sessionRolesKeys.all, sessionId] as const,
};
